import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useForm } from "react-hook-form";
import { X, Loader2, Camera, ImagePlus, ScanLine, FileStack, FileText, ScanSearch, Aperture, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { EASE } from "../../components/landing/shared";

const inputCls =
  "w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder-slate-400 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20";
const labelCls = "block text-[10px] font-semibold tracking-[0.2em] uppercase text-slate-400 mb-2";
const errorCls = "text-xs text-red-500 mt-1.5";

function Field({ label, error, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
      {error && <p className={errorCls}>{error.message}</p>}
    </div>
  );
}

const RECORD_TYPES = [
  { key: "intraoral_photos", label: "Intraoral Photos", accept: "image/*", icon: Camera },
  { key: "extraoral_photos", label: "Extraoral Photos", accept: "image/*", icon: ImagePlus },
  { key: "occlusal", label: "Occlusal", accept: "image/*", icon: Aperture },
  { key: "xrays", label: "X-rays", accept: "image/*,.pdf,.dcm", icon: ScanLine },
  { key: "scans", label: "Scans", accept: "image/*,.pdf", icon: FileStack },
  { key: "documents", label: "Documents", accept: ".pdf,.txt,image/*", icon: FileText },
  { key: "other", label: "Other", accept: "image/*,.pdf,.txt", icon: Paperclip },
];

const EMPTY_FORM = {
  first_name: "", last_name: "", display_id: "", date_of_birth: "",
  gender: "female", phone: "", email: "",
  treatment_type: "Clear Aligners", treatment_stage: 1, treatment_total_stages: 20,
  treatment_start_date: new Date().toISOString().slice(0, 10),
  assigned_doctor: "", clinical_notes: "", status: "active",
};

export default function AddPatientModal({ open, onClose, onCreated, onSaved, patient, existingRecords = [] }) {
  const { user } = useAuth();
  const isEdit = Boolean(patient);
  const [serverError, setServerError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploadNote, setUploadNote] = useState("");
  const [files, setFiles] = useState([]);
  const [recordStage, setRecordStage] = useState("1");
  const [recordDescription, setRecordDescription] = useState("");
  const fileInputs = useRef({});
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm({
    defaultValues: EMPTY_FORM,
  });
  const watchedTotalStages = Number(watch("treatment_total_stages") || 1);
  const totalStages = Number.isFinite(watchedTotalStages) && watchedTotalStages > 0 ? watchedTotalStages : 1;

  useEffect(() => {
    if (!open) return;
    if (patient) {
      reset({
        first_name: patient.first_name || "",
        last_name: patient.last_name || "",
        display_id: patient.display_id || "",
        date_of_birth: patient.date_of_birth || "",
        gender: patient.gender || "other",
        phone: patient.phone || "",
        email: patient.email || "",
        treatment_type: patient.treatment_type || "Clear Aligners",
        treatment_stage: patient.treatment_stage || 1,
        treatment_total_stages: patient.treatment_total_stages || 20,
        treatment_start_date: patient.treatment_start_date || EMPTY_FORM.treatment_start_date,
        assigned_doctor: patient.assigned_doctor || user?.name || "",
        clinical_notes: patient.clinical_notes || "",
        status: patient.status || "active",
      });
      setRecordStage(String(patient.treatment_stage || 1));
    } else {
      reset(EMPTY_FORM);
      setRecordStage(String(EMPTY_FORM.treatment_stage));
      if (user?.name) setValue("assigned_doctor", user.name);
    }
    setRecordDescription("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, patient]);

  const addFiles = (category, fileList) => {
    const stage = Math.min(Math.max(Number(recordStage || 1), 1), totalStages);
    const next = Array.from(fileList).map((f) => ({
      id: `${category}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      category,
      stage,
      description: recordDescription.trim(),
      file: f,
    }));
    setFiles((prev) => [...prev, ...next]);
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const close = () => {
    if (submitting) return;
    setServerError("");
    setUploadNote("");
    setFiles([]);
    setRecordStage("1");
    setRecordDescription("");
    reset();
    onClose();
  };

  const submit = async (values, startReview) => {
    setSubmitting(true);
    setServerError("");
    setUploadNote("");
    try {
      let targetPatientId;
      if (isEdit) {
        await api.put(`/patients/${patient.patient_id}`, {
          ...values,
          treatment_stage: Number(values.treatment_stage),
          treatment_total_stages: Number(values.treatment_total_stages),
        });
        targetPatientId = patient.patient_id;
      } else {
        const { data: created } = await api.post("/patients", {
          ...values,
          treatment_stage: Number(values.treatment_stage),
          treatment_total_stages: Number(values.treatment_total_stages),
          start_review: startReview,
        });
        targetPatientId = created.patient_id;
      }
      let failed = 0;
      for (let i = 0; i < files.length; i++) {
        setUploadNote(`Uploading records ${i + 1} of ${files.length}…`);
        const fd = new FormData();
        fd.append("file", files[i].file);
        try {
          await api.post(`/patients/${targetPatientId}/records`, fd, {
            params: {
              category: files[i].category,
              stage: files[i].stage,
              description: files[i].description,
            },
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch {
          failed += 1;
        }
      }
      if (failed > 0) {
        toast.warning(`Saved, but ${failed} file(s) could not be uploaded. Reattach them from the patient record.`);
      }
      reset();
      setFiles([]);
      setUploadNote("");
      setRecordDescription("");
      if (isEdit) {
        onSaved?.();
      } else {
        onCreated(startReview);
      }
    } catch (e) {
      setServerError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm"
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.4, ease: EASE }}
              data-testid="add-patient-modal"
              className="pointer-events-auto w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white border border-slate-200 rounded-3xl p-8 shadow-[0_40px_90px_-20px_rgba(15,23,42,0.25)]"
            >
              <div className="flex items-center justify-between mb-7">
                <div>
                  <h2 className="font-display text-2xl tracking-tight text-[#0F172A]">{isEdit ? "Edit Patient" : "Add Patient"}</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    {isEdit ? `Update details, notes, treatment and records for ${patient?.display_id}.` : "A treatment record is created automatically."}
                  </p>
                </div>
                <button
                  type="button"
                  data-testid="add-patient-close"
                  onClick={close}
                  className="w-9 h-9 rounded-xl border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" strokeWidth={1.8} />
                </button>
              </div>

              <form onSubmit={handleSubmit((v) => submit(v, false))} className="grid sm:grid-cols-2 gap-5" data-testid="add-patient-form">
                <p className="sm:col-span-2 text-[10px] font-bold tracking-[0.25em] uppercase text-[#2563EB]">Patient Details</p>
                <Field label="First name" error={errors.first_name}>
                  <input data-testid="ap-first-name" className={inputCls} placeholder="Emily"
                    {...register("first_name", { required: "First name is required." })} />
                </Field>
                <Field label="Last name" error={errors.last_name}>
                  <input data-testid="ap-last-name" className={inputCls} placeholder="Rodriguez"
                    {...register("last_name", { required: "Last name is required." })} />
                </Field>
                <Field label="Patient ID" error={errors.display_id}>
                  <input data-testid="ap-display-id" className={`${inputCls} ${isEdit ? "opacity-50 cursor-not-allowed" : ""}`} placeholder="ALX-1009"
                    disabled={isEdit}
                    {...register("display_id", { required: "Patient ID is required.", minLength: { value: 3, message: "At least 3 characters." } })} />
                </Field>
                <Field label="Date of birth" error={errors.date_of_birth}>
                  <input data-testid="ap-dob" type="date" className={inputCls}
                    {...register("date_of_birth", { required: "Date of birth is required." })} />
                </Field>
                <Field label="Gender">
                  <select data-testid="ap-gender" className={inputCls} {...register("gender")}>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                    <option value="other">Other</option>
                  </select>
                </Field>
                <Field label="Phone">
                  <input data-testid="ap-phone" className={inputCls} placeholder="+1 555-0107" {...register("phone")} />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Email" error={errors.email}>
                    <input data-testid="ap-email" type="email" className={inputCls} placeholder="patient@email.com"
                      {...register("email", { pattern: { value: /^\S+@\S+\.\S+$/, message: "Enter a valid email." } })} />
                  </Field>
                </div>

                <p className="sm:col-span-2 text-[10px] font-bold tracking-[0.25em] uppercase text-[#2563EB] pt-3 border-t border-slate-100">Treatment Information</p>
                <Field label="Treatment type">
                  <select data-testid="ap-treatment-type" className={inputCls} {...register("treatment_type")}>
                    <option>Clear Aligners</option>
                    <option>Braces</option>
                    <option>Retainer</option>
                  </select>
                </Field>
                <Field label="Treatment start date" error={errors.treatment_start_date}>
                  <input data-testid="ap-start-date" type="date" className={inputCls} {...register("treatment_start_date")} />
                </Field>
                <Field label="Current stage" error={errors.treatment_stage}>
                  <input data-testid="ap-stage" type="number" min="1" className={inputCls}
                    {...register("treatment_stage", { required: "Required.", min: { value: 1, message: "Must be at least 1." } })} />
                </Field>
                <Field label="Total stages" error={errors.treatment_total_stages}>
                  <input data-testid="ap-total-stages" type="number" min="1" className={inputCls}
                    {...register("treatment_total_stages", { required: "Required.", min: { value: 1, message: "Must be at least 1." } })} />
                </Field>
                <Field label="Assigned doctor">
                  <input data-testid="ap-doctor" className={inputCls} placeholder="Dr. Sarah Chen" {...register("assigned_doctor")} />
                </Field>
                <Field label="Status">
                  <select data-testid="ap-status" className={inputCls} {...register("status")}>
                    <option value="active">Active</option>
                    <option value="on_hold">On hold</option>
                    <option value="completed">Completed</option>
                  </select>
                </Field>

                <p className="sm:col-span-2 text-[10px] font-bold tracking-[0.25em] uppercase text-[#2563EB] pt-3 border-t border-slate-100">Clinical Notes</p>
                <div className="sm:col-span-2">
                  <textarea
                    data-testid="ap-clinical-notes"
                    rows={4}
                    className={`${inputCls} resize-none leading-relaxed`}
                    placeholder="Chief complaint, initial clinical observations, relevant history, treatment context…"
                    {...register("clinical_notes")}
                  />
                </div>

                <p className="sm:col-span-2 text-[10px] font-bold tracking-[0.25em] uppercase text-[#2563EB] pt-3 border-t border-slate-100">
                  {isEdit ? "Patient Records" : "Initial Records"}
                </p>
                <div className="sm:col-span-2">
                  {isEdit && existingRecords.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3" data-testid="ap-existing-records">
                      {existingRecords.map((rec) => (
                        <span key={rec.record_id} className="inline-flex items-center gap-2 rounded-full bg-[#2563EB]/5 border border-[#2563EB]/20 px-3 py-1.5 text-xs text-slate-600">
                          <FileText className="w-3 h-3 text-[#2563EB]" />
                          {rec.file_name}
                          {rec.treatment_stage && <span className="text-[10px] text-[#1D4ED8]">Stage {rec.treatment_stage}</span>}
                          <span className="text-[10px] text-slate-400">on file</span>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="grid sm:grid-cols-[150px_1fr] gap-3 mb-3" data-testid="ap-record-metadata">
                    <div>
                      <label className={labelCls}>Record stage</label>
                      <input
                        type="number"
                        min="1"
                        max={totalStages}
                        data-testid="ap-record-stage"
                        className={inputCls}
                        value={recordStage}
                        onChange={(e) => setRecordStage(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Record note</label>
                      <input
                        data-testid="ap-record-description"
                        className={inputCls}
                        placeholder="Optional note applied to the selected files"
                        value={recordDescription}
                        onChange={(e) => setRecordDescription(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {RECORD_TYPES.map((rt) => (
                      <div key={rt.key}>
                        <input
                          ref={(el) => (fileInputs.current[rt.key] = el)}
                          type="file"
                          accept={rt.accept}
                          multiple
                          className="hidden"
                          data-testid={`ap-upload-${rt.key}`}
                          onChange={(e) => {
                            addFiles(rt.key, e.target.files);
                            e.target.value = "";
                          }}
                        />
                        <button
                          type="button"
                          data-testid={`ap-upload-btn-${rt.key}`}
                          onClick={() => fileInputs.current[rt.key]?.click()}
                          className="w-full flex flex-col items-center gap-1.5 rounded-xl border border-dashed border-slate-300 px-2 py-4 text-[11px] font-medium text-slate-500 transition-colors duration-200 hover:border-[#2563EB]/50 hover:text-[#1D4ED8] hover:bg-[#2563EB]/5"
                        >
                          <rt.icon className="w-4 h-4" strokeWidth={1.7} />
                          {rt.label}
                        </button>
                      </div>
                    ))}
                  </div>
                  {files.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3" data-testid="ap-files-list">
                      {files.map((f) => (
                        <span key={f.id} className="inline-flex items-center gap-2 rounded-full bg-slate-100 border border-slate-200 px-3 py-1.5 text-xs text-slate-600">
                          {f.file.name}
                          <span className="text-[10px] font-semibold text-[#1D4ED8]">Stage {f.stage}</span>
                          <button type="button" onClick={() => removeFile(f.id)} data-testid={`ap-remove-file-${f.id}`} aria-label="Remove file">
                            <X className="w-3 h-3 text-slate-400 hover:text-red-500 transition-colors" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {serverError && (
                  <p data-testid="add-patient-error" className="sm:col-span-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                    {serverError}
                  </p>
                )}
                {uploadNote && (
                  <p className="sm:col-span-2 flex items-center gap-2 text-xs text-slate-500" data-testid="ap-upload-progress">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-[#2563EB]" />
                    {uploadNote}
                  </p>
                )}

                <div className="sm:col-span-2 flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    type="button"
                    data-testid="add-patient-cancel"
                    onClick={close}
                    className="flex-1 rounded-full border border-slate-200 py-3.5 text-sm font-semibold text-slate-500 hover:text-slate-800 hover:border-slate-300 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    data-testid="add-patient-submit"
                    disabled={submitting}
                    className={`flex-1 rounded-full py-3.5 text-sm font-semibold transition-[box-shadow,background-color] duration-300 disabled:opacity-60 flex items-center justify-center gap-2 ${
                      isEdit
                        ? "bg-gradient-to-r from-[#2563EB] to-[#6366F1] text-white shadow-[0_8px_20px_rgba(37,99,235,0.25)] hover:shadow-[0_14px_30px_rgba(99,102,241,0.35)]"
                        : "border border-[#2563EB]/40 text-[#1D4ED8] hover:bg-[#2563EB]/5"
                    }`}
                  >
                    {submitting && !uploadNote && <Loader2 className="w-4 h-4 animate-spin" />}
                    {isEdit ? "Save Changes" : "Save Patient"}
                  </button>
                  {!isEdit && (
                    <button
                      type="button"
                      data-testid="add-patient-submit-review"
                      disabled={submitting}
                      onClick={handleSubmit((v) => submit(v, true))}
                      className="flex-1 rounded-full bg-gradient-to-r from-[#2563EB] to-[#6366F1] py-3.5 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(37,99,235,0.25)] hover:shadow-[0_14px_30px_rgba(99,102,241,0.35)] transition-shadow duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanSearch className="w-4 h-4" strokeWidth={2} />}
                      Save &amp; Start AI Review
                    </button>
                  )}
                </div>
              </form>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
